import { useCallback, useEffect, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useAuth } from "../../../context/AuthContext";
import { InvoiceRow, InvoicesResponse } from "../../../types/subscription";
import InvoiceActionsMenu from "./InvoiceActionsMenu";

const fmtMoney = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(n);
};

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const isoDay = (iso: string | null | undefined): string => {
  if (!iso) return "";
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const STATUS_STYLES: Record<string, { bg: string; fg: string }> = {
  PAID: { bg: "#d4edda", fg: "#155724" },
  COMPLETED: { bg: "#d4edda", fg: "#155724" },
  CREATED: { bg: "#e7f1ff", fg: "#0a58ca" },
  PENDING: { bg: "#fff3cd", fg: "#856404" },
  FAILED: { bg: "#f8d7da", fg: "#721c24" },
  UNPAID: { bg: "#f8d7da", fg: "#721c24" },
  REJECTED: { bg: "#fde2e2", fg: "#b32424" },
  REJECTED_PAUSED: { bg: "#fff3cd", fg: "#856404" },
  EXCEEDED_PAYMENT_RETRIES: { bg: "#7c1d1d", fg: "white" },
  DISPUTED: { bg: "#f3e8ff", fg: "#6f42c1" },
};

function InvoiceStatusPill({ status }: { status: string }) {
  const s = STATUS_STYLES[(status ?? "").toUpperCase()] ?? {
    bg: "#e9ecef",
    fg: "#495057",
  };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "0.12rem 0.5rem",
        borderRadius: "999px",
        fontSize: ".7rem",
        fontWeight: 700,
        letterSpacing: ".3px",
        background: s.bg,
        color: s.fg,
        whiteSpace: "nowrap",
      }}
    >
      {status || "—"}
    </span>
  );
}

const PAGE_SIZE = 50;

// Mirrors the domain rule in DomainScheduledInvoice::updateFinalPrice — the
// backend rejects edits on any other status with WrongScheduledInvoiceStatusException.
// Gating the inputs client-side prevents wasted requests + misleading
// "N facturas fallaron" toast when bulk-editing a page that mixes
// CREATED/PAUSED rows with terminal ones (COMPLETED, REJECTED, etc).
const EDITABLE_FINAL_PRICE_STATUSES = new Set(["CREATED", "PAUSED"]);

const canEditFinalPrice = (status: string | null | undefined): boolean =>
  EDITABLE_FINAL_PRICE_STATUSES.has((status ?? "").toUpperCase());

type Props = {
  subscriptionId: string;
};

export default function InvoicesTable({ subscriptionId }: Props) {
  const { token } = useAuth();
  const [data, setData] = useState<InvoicesResponse | null>(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkPrices, setBulkPrices] = useState<Record<string, string>>({});
  const [savingBulk, setSavingBulk] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(page * PAGE_SIZE));
      selectedStatuses.forEach((s) => params.append("status[]", s));
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo) params.set("dateTo", dateTo);
      const response = await request<InvoicesResponse>(
        `/api/admin/operations/subscriptions/${subscriptionId}/invoices?${params.toString()}`,
        { token },
      );
      setData(response);
    } catch (err) {
      setError(err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [subscriptionId, token, page, selectedStatuses, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const beginBulk = () => {
    if (!data) return;
    const map: Record<string, string> = {};
    data.data.forEach((row) => {
      if (!canEditFinalPrice((row.status ?? row.stripeStatus))) return;
      // Bulk edit operates on the final price (what we actually charge); seed
      // with finalPrice and fall back to rawInvoice when no final price has
      // been set yet so the field is never blank for fresh invoices.
      const seed = row.finalPrice ?? row.rawInvoice;
      map[row.invoiceId] = String(seed);
    });
    setBulkPrices(map);
    setBulkMode(true);
  };

  const cancelBulk = () => {
    setBulkPrices({});
    setBulkMode(false);
  };

  const saveBulk = async () => {
    if (!data || savingBulk) return;
    setSavingBulk(true);
    setError(null);
    let failures = 0;
    for (const row of data.data) {
      if (!canEditFinalPrice((row.status ?? row.stripeStatus))) continue;
      const raw = bulkPrices[row.invoiceId];
      if (raw === undefined) continue;
      const parsed = parseFloat(raw.replace(",", "."));
      if (Number.isNaN(parsed)) {
        failures++;
        continue;
      }
      const current = row.finalPrice ?? row.rawInvoice;
      if (parsed === current) continue;
      try {
        await request(
          `/api/admin/operations/invoices/${row.invoiceId}/final-price`,
          {
            method: "PATCH",
            token,
            body: { newPrice: parsed },
          },
        );
      } catch {
        failures++;
      }
    }
    setSavingBulk(false);
    setBulkMode(false);
    setBulkPrices({});
    await load();
    if (failures > 0) {
      setError(`${failures} factura(s) fallaron al guardar`);
    }
  };

  if (loading && !data) {
    return (
      <div style={{ padding: "1rem", color: "#73879C" }}>Cargando facturas…</div>
    );
  }
  if (error && !data) {
    return (
      <div
        style={{
          padding: "0.75rem 1rem",
          background: "#fde2e2",
          color: "#b32424",
          borderRadius: "0.5rem",
        }}
      >
        {error}
      </div>
    );
  }
  if (!data) return null;

  const total = data.recordsTotal;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div
      style={{
        background: "white",
        borderRadius: "0.6rem",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        overflow: "hidden",
      }}
    >
      <header
        style={{
          padding: "0.7rem 1rem",
          borderBottom: "1px solid #f1f3f5",
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: ".82rem",
            fontWeight: 700,
            color: "#1f2933",
            textTransform: "uppercase",
            letterSpacing: ".4px",
          }}
        >
          Facturas
        </h3>
        <span style={{ fontSize: ".75rem", color: "#73879C" }}>
          {total} total
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: "0.4rem" }}>
          {bulkMode ? (
            <>
              <button
                type="button"
                onClick={cancelBulk}
                disabled={savingBulk}
                style={pagerBtn}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={saveBulk}
                disabled={savingBulk}
                style={primaryBtn}
              >
                {savingBulk ? "Guardando…" : "Guardar precios"}
              </button>
            </>
          ) : (
            <button type="button" onClick={beginBulk} style={pagerBtn}>
              Bulk edit prices
            </button>
          )}
        </div>
      </header>
      {data && (
        <InvoiceAggregatesBar
          counts={data.aggregates.countsByStatus}
          totalAmount={data.aggregates.totalAmount}
          selectedStatuses={selectedStatuses}
          onToggleStatus={(s) => {
            setSelectedStatuses((prev) =>
              prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
            );
            setPage(0);
          }}
        />
      )}
      <div
        style={{
          padding: "0.55rem 1rem",
          borderBottom: "1px solid #f1f3f5",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "0.55rem",
          fontSize: ".75rem",
        }}
      >
        <span style={{ color: "#73879C", fontWeight: 600 }}>Fecha factura</span>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setPage(0);
          }}
          style={dateInputStyle}
        />
        <span style={{ color: "#73879C" }}>→</span>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setPage(0);
          }}
          style={dateInputStyle}
        />
        {(selectedStatuses.length > 0 || dateFrom || dateTo) && (
          <button
            type="button"
            onClick={() => {
              setSelectedStatuses([]);
              setDateFrom("");
              setDateTo("");
              setPage(0);
            }}
            style={{
              padding: "0.3rem 0.7rem",
              borderRadius: "0.35rem",
              background: "white",
              color: "#73879C",
              border: "1px solid #dee2e6",
              fontSize: ".72rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Limpiar filtros
          </button>
        )}
      </div>
      {error && (
        <div
          style={{
            padding: "0.6rem 1rem",
            background: "#fde2e2",
            color: "#b32424",
            fontSize: ".82rem",
          }}
        >
          {error}
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".82rem" }}>
          <thead>
            <tr style={{ background: "#f8f9fb" }}>
              <Th>Annuality</Th>
              <Th>Nº</Th>
              <Th>Fecha</Th>
              <Th style={{ textAlign: "right" }}>Raw price</Th>
              <Th style={{ textAlign: "right" }}>Final price</Th>
              <Th>Status</Th>
              <Th>Cobrada</Th>
              <Th>Invoice ID</Th>
              <Th style={{ textAlign: "right" }}>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {data.data.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  style={{ padding: "0.85rem", color: "#73879C", textAlign: "center" }}
                >
                  Sin facturas
                </td>
              </tr>
            ) : (
              data.data.map((row) => (
                <InvoiceRowEl
                  key={row.invoiceId}
                  row={row}
                  bulkMode={bulkMode}
                  bulkPrice={bulkPrices[row.invoiceId] ?? ""}
                  onBulkPriceChange={(v) =>
                    setBulkPrices((prev) => ({ ...prev, [row.invoiceId]: v }))
                  }
                  onReload={load}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div
          style={{
            padding: "0.5rem 1rem",
            borderTop: "1px solid #f1f3f5",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            style={pagerBtn}
          >
            ← Anterior
          </button>
          <span style={{ fontSize: ".75rem", color: "#73879C" }}>
            {page + 1} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            style={pagerBtn}
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
}

function InvoiceRowEl({
  row,
  bulkMode,
  bulkPrice,
  onBulkPriceChange,
  onReload,
}: {
  row: InvoiceRow;
  bulkMode: boolean;
  bulkPrice: string;
  onBulkPriceChange: (v: string) => void;
  onReload: () => void;
}) {
  const { token } = useAuth();
  const [editing, setEditing] = useState<"price" | "completed" | "date" | null>(
    null,
  );
  const [draft, setDraft] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const startEdit = (kind: "price" | "completed" | "date") => {
    if (kind === "price") setDraft(String(row.rawInvoice));
    if (kind === "completed") setDraft(String(row.finalPrice ?? row.rawInvoice));
    if (kind === "date") setDraft(isoDay(row.invoiceDate));
    setEditing(kind);
    setRowError(null);
  };

  const cancel = () => {
    setEditing(null);
    setDraft("");
    setRowError(null);
  };

  const saveEdit = async () => {
    if (!editing || submitting) return;
    setSubmitting(true);
    setRowError(null);
    try {
      if (editing === "price") {
        const parsed = parseFloat(draft.replace(",", "."));
        if (Number.isNaN(parsed)) {
          setRowError("Precio inválido");
          setSubmitting(false);
          return;
        }
        await request(
          `/api/admin/operations/invoices/${row.invoiceId}/final-price`,
          { method: "PATCH", token, body: { newPrice: parsed } },
        );
      } else if (editing === "completed") {
        const parsed = parseFloat(draft.replace(",", "."));
        if (Number.isNaN(parsed)) {
          setRowError("Precio inválido");
          setSubmitting(false);
          return;
        }
        await request(
          `/api/admin/operations/invoices/${row.invoiceId}/completed-price`,
          { method: "PATCH", token, body: { newPrice: parsed } },
        );
      } else if (editing === "date") {
        if (!draft) {
          setRowError("Fecha requerida");
          setSubmitting(false);
          return;
        }
        await request(
          `/api/admin/operations/invoices/${row.invoiceId}/expected-date`,
          { method: "PATCH", token, body: { newExpectedDate: draft } },
        );
      }
      setEditing(null);
      onReload();
    } catch (err) {
      setRowError(
        err instanceof HttpError
          ? `HTTP ${err.status}: ${((err.body as { message?: string })?.message ?? "").slice(0, 200)}`
          : "Guardar falló",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <tr style={{ borderBottom: "1px solid #f1f3f5" }}>
      <Td mono>{row.annuality}</Td>
      <Td mono>{row.invoiceNumber}</Td>
      <Td>
        {editing === "date" ? (
          <InlineEdit
            type="date"
            value={draft}
            onChange={setDraft}
            onSave={saveEdit}
            onCancel={cancel}
            submitting={submitting}
          />
        ) : (
          <button type="button" onClick={() => startEdit("date")} style={cellEditBtn}>
            {fmtDate(row.invoiceDate)}
          </button>
        )}
      </Td>
      <Td mono style={{ textAlign: "right" }}>
        {editing === "price" ? (
          <InlineEdit
            type="text"
            value={draft}
            onChange={setDraft}
            onSave={saveEdit}
            onCancel={cancel}
            submitting={submitting}
          />
        ) : (
          <button type="button" onClick={() => startEdit("price")} style={cellEditBtn}>
            {fmtMoney(row.rawInvoice)}
          </button>
        )}
      </Td>
      <Td mono style={{ textAlign: "right" }}>
        {bulkMode ? (
          canEditFinalPrice((row.status ?? row.stripeStatus)) ? (
            <input
              type="text"
              value={bulkPrice}
              onChange={(e) => onBulkPriceChange(e.target.value)}
              style={bulkPriceInput}
            />
          ) : (
            <span
              style={{ color: "#adb5bd" }}
              title="No editable: la factura ya no está en estado CREATED/PAUSED"
            >
              {fmtMoney(row.finalPrice)}
            </span>
          )
        ) : editing === "completed" ? (
          <InlineEdit
            type="text"
            value={draft}
            onChange={setDraft}
            onSave={saveEdit}
            onCancel={cancel}
            submitting={submitting}
          />
        ) : (
          <button type="button" onClick={() => startEdit("completed")} style={cellEditBtn}>
            {fmtMoney(row.finalPrice)}
          </button>
        )}
      </Td>
      <Td>
        <InvoiceStatusPill status={(row.status ?? row.stripeStatus)} />
      </Td>
      <Td>{fmtDate(row.stripePaidDate)}</Td>
      <Td>
        <span style={{ fontSize: ".72rem", color: "#73879C", fontFamily: "monospace" }}>
          {row.invoiceId}
        </span>
      </Td>
      <Td style={{ textAlign: "right" }}>
        {!bulkMode && (
          <InvoiceActionsMenu
            invoiceId={row.invoiceId}
            market={row.market}
            onReload={onReload}
          />
        )}
        {rowError && (
          <div
            style={{
              marginTop: 4,
              fontSize: ".7rem",
              color: "#b32424",
              textAlign: "right",
            }}
          >
            {rowError}
          </div>
        )}
      </Td>
    </tr>
  );
}

function InlineEdit({
  type,
  value,
  onChange,
  onSave,
  onCancel,
  submitting,
}: {
  type: string;
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
  submitting: boolean;
}) {
  return (
    <span style={{ display: "inline-flex", gap: "0.25rem", alignItems: "center" }}>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus
        style={inlineInputStyle}
      />
      <button
        type="button"
        onClick={onSave}
        disabled={submitting}
        style={inlineSaveBtn}
      >
        ✓
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={submitting}
        style={inlineCancelBtn}
      >
        ✕
      </button>
    </span>
  );
}

function Th({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <th
      style={{
        padding: "0.5rem 0.75rem",
        textAlign: "left",
        fontSize: ".7rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: ".4px",
        color: "#73879C",
        borderBottom: "1px solid #e9ecef",
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  mono,
  style,
}: {
  children: React.ReactNode;
  mono?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <td
      style={{
        padding: "0.55rem 0.75rem",
        fontFamily: mono ? "monospace" : "inherit",
        color: "#1f2933",
        verticalAlign: "middle",
        ...style,
      }}
    >
      {children}
    </td>
  );
}

function InvoiceAggregatesBar({
  counts,
  totalAmount,
  selectedStatuses,
  onToggleStatus,
}: {
  counts: Record<string, number>;
  totalAmount: number;
  selectedStatuses: string[];
  onToggleStatus: (status: string) => void;
}) {
  const orderedStatuses = Object.keys(counts).sort();
  return (
    <div
      style={{
        padding: "0.55rem 1rem",
        borderBottom: "1px solid #f1f3f5",
        display: "flex",
        flexWrap: "wrap",
        gap: "0.4rem",
        alignItems: "center",
      }}
    >
      <span style={{ color: "#73879C", fontSize: ".72rem", fontWeight: 600 }}>
        Por estado
      </span>
      {orderedStatuses.length === 0 && (
        <span style={{ color: "#73879C", fontSize: ".75rem" }}>—</span>
      )}
      {orderedStatuses.map((status) => {
        const active = selectedStatuses.includes(status);
        const palette = STATUS_STYLES[status.toUpperCase()] ?? {
          bg: "#e9ecef",
          fg: "#495057",
        };
        return (
          <button
            key={status}
            type="button"
            onClick={() => onToggleStatus(status)}
            style={{
              padding: "0.2rem 0.55rem",
              borderRadius: "999px",
              border: `1px solid ${active ? palette.fg : "transparent"}`,
              background: active ? palette.bg : "#f8f9fb",
              color: active ? palette.fg : "#1f2933",
              fontSize: ".72rem",
              fontWeight: 600,
              cursor: "pointer",
              display: "inline-flex",
              gap: "0.3rem",
              alignItems: "center",
            }}
          >
            <span>{status}</span>
            <span
              style={{
                background: palette.bg,
                color: palette.fg,
                padding: "0 0.35rem",
                borderRadius: "999px",
                fontSize: ".7rem",
                fontWeight: 700,
              }}
            >
              {counts[status]}
            </span>
          </button>
        );
      })}
      <span style={{ marginLeft: "auto", color: "#73879C", fontSize: ".72rem" }}>
        Importe total:{" "}
        <strong style={{ color: "#1f2933" }}>
          {new Intl.NumberFormat("es-ES", {
            style: "currency",
            currency: "EUR",
            maximumFractionDigits: 2,
          }).format(totalAmount)}
        </strong>
      </span>
    </div>
  );
}

const dateInputStyle: React.CSSProperties = {
  padding: "0.3rem 0.5rem",
  borderRadius: "0.35rem",
  border: "1px solid #dee2e6",
  background: "#f8f9fb",
  fontSize: ".78rem",
};

const pagerBtn: React.CSSProperties = {
  padding: "0.3rem 0.65rem",
  borderRadius: "0.35rem",
  background: "white",
  border: "1px solid #dee2e6",
  fontSize: ".75rem",
  color: "#1f2933",
  cursor: "pointer",
};

const primaryBtn: React.CSSProperties = {
  padding: "0.3rem 0.85rem",
  borderRadius: "0.35rem",
  background: "#0069d9",
  border: "1px solid #0069d9",
  fontSize: ".75rem",
  color: "white",
  fontWeight: 600,
  cursor: "pointer",
};

const cellEditBtn: React.CSSProperties = {
  background: "transparent",
  color: "#1f2933",
  border: "1px dashed transparent",
  padding: "0.15rem 0.35rem",
  borderRadius: "0.3rem",
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: "inherit",
};

const inlineInputStyle: React.CSSProperties = {
  width: 96,
  padding: "0.2rem 0.35rem",
  borderRadius: "0.3rem",
  border: "1px solid #dee2e6",
  background: "#f8f9fb",
  fontSize: ".8rem",
  fontFamily: "monospace",
};

const inlineSaveBtn: React.CSSProperties = {
  padding: "0.15rem 0.4rem",
  background: "#1a7f37",
  color: "white",
  border: "1px solid #1a7f37",
  borderRadius: "0.3rem",
  cursor: "pointer",
  fontSize: ".75rem",
};

const inlineCancelBtn: React.CSSProperties = {
  padding: "0.15rem 0.4rem",
  background: "white",
  color: "#73879C",
  border: "1px solid #dee2e6",
  borderRadius: "0.3rem",
  cursor: "pointer",
  fontSize: ".75rem",
};

const bulkPriceInput: React.CSSProperties = {
  width: 96,
  padding: "0.2rem 0.35rem",
  borderRadius: "0.3rem",
  border: "1px solid #856404",
  background: "#fff8e1",
  fontSize: ".8rem",
  fontFamily: "monospace",
  textAlign: "right",
};
